import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog"]
  static values = {
    openOnConnect: Boolean
  }

  connect() {
    this.closeBeforeCache = () => this.closeDialog({ animate: false })
    this.cancelDialog = (event) => {
      event.preventDefault()
      this.closeDialog()
    }

    if (this.hasDialogTarget) {
      this.dialogTarget.addEventListener("cancel", this.cancelDialog)
    }

    document.addEventListener("turbo:before-cache", this.closeBeforeCache)

    if (this.openOnConnectValue) {
      this.openFrameId = window.requestAnimationFrame(() => {
        this.openDialog()
        this.openFrameId = undefined
      })
    }
  }

  disconnect() {
    if (this.hasDialogTarget) {
      this.dialogTarget.removeEventListener("cancel", this.cancelDialog)
    }

    document.removeEventListener("turbo:before-cache", this.closeBeforeCache)
    window.clearTimeout(this.closeFallbackTimer)
    if (this.openFrameId) window.cancelAnimationFrame(this.openFrameId)
    this.restoreBodyScroll()
    this.restoreFocus()
  }

  open(event) {
    event.preventDefault()

    this.openDialog(event.currentTarget)
  }

  openDialog(opener = null) {
    if (!this.hasDialogTarget || this.dialogTarget.open || this.isClosing) return

    if (opener instanceof HTMLElement) this.openerElement = opener
    this.lockBodyScroll()
    this.dialogTarget.classList.remove("home-estimate-dialog--closing")
    this.dialogTarget.showModal()
  }

  close(event) {
    event.preventDefault()
    this.closeDialog()
  }

  closeFromBackdrop(event) {
    if (event.target === event.currentTarget) {
      this.closeDialog()
    }
  }

  afterClose() {
    this.isClosing = false
    if (this.hasDialogTarget) this.dialogTarget.classList.remove("home-estimate-dialog--closing")
    window.clearTimeout(this.closeFallbackTimer)
    this.restoreBodyScroll()
    this.restoreFocus()
  }

  closeDialog({ animate = true } = {}) {
    if (!this.hasDialogTarget || !this.dialogTarget.open || this.isClosing) return

    if (!animate || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.dialogTarget.close()
      return
    }

    this.isClosing = true
    this.dialogTarget.classList.add("home-estimate-dialog--closing")

    const panel = this.dialogTarget.querySelector(".home-estimate-dialog__panel")
    if (!panel) {
      this.dialogTarget.close()
      return
    }

    const finishClose = () => {
      window.clearTimeout(this.closeFallbackTimer)
      if (this.hasDialogTarget && this.dialogTarget.open) {
        this.dialogTarget.close()
      }
    }

    this.closeFallbackTimer = window.setTimeout(finishClose, 420)
  }

  lockBodyScroll() {
    this.previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
  }

  restoreBodyScroll() {
    if (this.previousBodyOverflow === undefined) return

    document.body.style.overflow = this.previousBodyOverflow
    this.previousBodyOverflow = undefined
  }

  restoreFocus() {
    if (!this.openerElement?.isConnected) return

    this.openerElement.focus()
    this.openerElement = undefined
  }
}
